<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeApi } from '@/entities/store'
import { useCustomToast } from '@/shared/lib'
import CButton from './CButton.vue'
import CDialog from './CDialog.vue'

const { t } = useI18n()
const queryClient = useQueryClient()
const { success, error } = useCustomToast()

const WB_TOKEN_URL
  = 'https://seller.wildberries.ru/supplier-settings/access-to-api'

const wbCategories = computed(() => [
  { label: t('api_method_content'), allowed: true },
  { label: t('api_method_marketplace'), allowed: true },
  { label: t('api_method_statistics'), allowed: true },
  { label: t('wb_category_finances'), allowed: true },
  { label: t('api_method_analytics'), allowed: true },
  { label: t('api_method_promotion'), allowed: true },
  { label: t('api_method_feedbacks'), allowed: true },
  { label: t('api_method_prices'), allowed: true },
  { label: t('wb_category_chat'), allowed: true },
  { label: t('api_method_supplies'), allowed: true },
  { label: t('wb_category_returns'), allowed: true },
  { label: t('wb_category_documents'), allowed: true },
  { label: t('wb_category_users'), allowed: false },
])

const wbTabs = computed(() => [
  { label: t('wb_tab_catalog'), selected: false },
  { label: t('wb_tab_manual'), selected: true },
])

const tokenTypes = computed(() => [
  { label: t('wb_token_type_basic'), selected: false },
  { label: t('wb_token_type_personal'), selected: true },
  { label: t('wb_token_type_test'), selected: false },
])

const navigationSteps = computed(() => [
  t('api_key_nav_step_profile'),
  t('api_key_nav_step_integrations'),
  t('api_key_nav_step_button'),
])

const accessLevels = computed(() => [
  { label: t('wb_access_read_write'), selected: true },
  { label: t('wb_access_read_only'), selected: false },
])

const securityPoints = computed(() => [
  t('api_key_security_no_cabinet'),
  t('api_key_security_encrypted'),
  t('api_key_security_scope'),
  t('api_key_security_purpose'),
  t('api_key_security_revoke'),
  t('api_key_security_no_share'),
])

const model = defineModel<boolean>()
const isLoading = ref(false)
const apiToken = ref('')
const showSecurity = ref(false)

function handleCancel() {
  model.value = false
  apiToken.value = ''
}

async function handleSave() {
  const trimmedToken = apiToken.value.trim()
  if (!trimmedToken)
    return

  try {
    isLoading.value = true
    await storeApi.updateApiKey({
      api_key: trimmedToken,
    })

    queryClient.invalidateQueries({ queryKey: ['store-detail'] })
    queryClient.invalidateQueries({ queryKey: ['my-stores'] })

    success({
      title: t('success'),
      message: t('api_key_updated_successfully'),
    })

    model.value = false
    apiToken.value = ''
  }
  catch (err) {
    console.error('API Key update error:', err)

    let isInvalidApiKeyError = false
    if (err && typeof err === 'object' && 'errorData' in err && err.errorData) {
      const errorData = err.errorData as { error?: string }
      isInvalidApiKeyError = errorData.error === 'invalid_api_key'
    }

    if (!isInvalidApiKeyError) {
      error({
        title: t('error'),
        message: t('failed_to_update_api_key'),
      })
    }
  }
  finally {
    isLoading.value = false
  }
}
</script>

<template>
  <CDialog
    v-model="model"
    :title="$t('change_api_key')"
    width="560px"
  >
    <template #header>
      <h2 class="text-lg font-semibold text-text-primary leading-[26px]">
        {{ $t("change_api_key") }}
      </h2>
    </template>

    <div class="max-h-[70vh] overflow-y-auto pr-1 -mr-1 space-y-4">
      <p class="text-sm text-text-regular leading-relaxed">
        {{ $t("api_key_wizard_subtitle") }}
      </p>

      <a
        :href="WB_TOKEN_URL"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-2 text-sm font-medium text-brand-primary hover:opacity-80 transition-opacity"
      >
        {{ $t("api_key_wizard_open_partners_button") }}
        <i class="icon-external-link-01 text-base" />
      </a>

      <ol class="space-y-5 mt-4">
        <li class="space-y-2">
          <div class="flex items-center gap-2">
            <span
              class="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-semibold flex items-center justify-center"
            >
              1
            </span>
            <h3 class="text-sm font-semibold text-text-primary">
              {{ $t("api_key_step_navigate_title") }}
            </h3>
          </div>
          <ul class="pl-8 space-y-1.5">
            <li
              v-for="(navStep, idx) in navigationSteps"
              :key="navStep"
              class="flex items-start gap-2 text-xs text-text-regular leading-relaxed"
            >
              <span
                class="flex-shrink-0 w-4 h-4 rounded-full bg-bg-sky-blue text-brand-primary text-[10px] font-semibold flex items-center justify-center mt-0.5"
              >
                {{ idx + 1 }}
              </span>
              <span v-html="navStep" />
            </li>
          </ul>
        </li>

        <li class="space-y-2">
          <div class="flex items-center gap-2">
            <span
              class="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-semibold flex items-center justify-center"
            >
              2
            </span>
            <h3 class="text-sm font-semibold text-text-primary">
              {{ $t("api_key_step_tab_and_type_title") }}
            </h3>
          </div>
          <p class="text-xs text-text-regular pl-8">
            {{ $t("api_key_step_tab_and_type_desc") }}
          </p>
          <div class="pl-8 space-y-2">
            <div class="rounded-lg bg-bg-light p-1 grid grid-cols-2 gap-1">
              <div
                v-for="tab in wbTabs"
                :key="tab.label"
                class="rounded-md px-2 py-1.5 text-center text-[11px] leading-tight transition-colors"
                :class="
                  tab.selected
                    ? 'bg-white text-text-primary font-semibold shadow-sm'
                    : 'text-text-secondary'
                "
              >
                {{ tab.label }}
              </div>
            </div>
            <div class="grid grid-cols-3 gap-2">
              <div
                v-for="type in tokenTypes"
                :key="type.label"
                class="rounded-lg border px-2 py-2 flex items-center gap-1.5 transition-colors"
                :class="
                  type.selected
                    ? 'border-brand-primary bg-brand-primary/5'
                    : 'border-border-light bg-bg-light'
                "
              >
                <span
                  class="flex-shrink-0 w-3 h-3 rounded-full border-2 flex items-center justify-center"
                  :class="
                    type.selected
                      ? 'border-brand-primary'
                      : 'border-text-secondary/40'
                  "
                >
                  <span
                    v-if="type.selected"
                    class="w-1.5 h-1.5 rounded-full bg-brand-primary"
                  />
                </span>
                <span
                  class="text-[11px] leading-none"
                  :class="
                    type.selected
                      ? 'text-text-primary font-medium'
                      : 'text-text-secondary'
                  "
                >
                  {{ type.label }}
                </span>
              </div>
            </div>
          </div>
        </li>

        <li class="space-y-2">
          <div class="flex items-center gap-2">
            <span
              class="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-semibold flex items-center justify-center"
            >
              3
            </span>
            <h3 class="text-sm font-semibold text-text-primary">
              {{ $t("api_key_step_categories_title") }}
            </h3>
          </div>
          <p class="text-xs text-text-regular pl-8">
            {{ $t("api_key_step_categories_desc") }}
          </p>
          <div class="pl-8">
            <div
              class="rounded-xl border border-primary-light-9 bg-bg-sky-blue/40 p-3 grid grid-cols-2 sm:grid-cols-3 gap-2"
            >
              <div
                v-for="category in wbCategories"
                :key="category.label"
                class="flex items-center gap-2 px-2 py-1.5 rounded-md"
                :class="
                  category.allowed
                    ? 'bg-additional-green/5'
                    : 'bg-additional-red/5'
                "
              >
                <span
                  class="flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center"
                  :class="
                    category.allowed
                      ? 'bg-additional-green border-additional-green'
                      : 'bg-white border-additional-red'
                  "
                >
                  <i
                    v-if="category.allowed"
                    class="icon-tick text-white text-[10px]"
                  />
                  <i
                    v-else
                    class="icon-close text-additional-red text-[10px]"
                  />
                </span>
                <span
                  class="text-[11px] leading-tight truncate"
                  :class="
                    category.allowed
                      ? 'text-text-primary'
                      : 'text-additional-red font-medium'
                  "
                >
                  {{ category.label }}
                </span>
              </div>
            </div>
            <p class="text-[11px] text-additional-red mt-1.5 flex items-center gap-1">
              <i class="icon-warning text-xs" />
              {{ $t("wb_category_users_warning") }}
            </p>
          </div>
        </li>

        <li class="space-y-2">
          <div class="flex items-center gap-2">
            <span
              class="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-semibold flex items-center justify-center"
            >
              4
            </span>
            <h3 class="text-sm font-semibold text-text-primary">
              {{ $t("api_key_step_access_title") }}
            </h3>
          </div>
          <p class="text-xs text-text-regular pl-8">
            {{ $t("api_key_step_access_desc") }}
          </p>
          <div class="pl-8 grid grid-cols-2 gap-2">
            <div
              v-for="level in accessLevels"
              :key="level.label"
              class="rounded-lg border px-3 py-2 flex items-center gap-2 transition-colors"
              :class="
                level.selected
                  ? 'border-brand-primary bg-brand-primary/5'
                  : 'border-border-light bg-bg-light'
              "
            >
              <span
                class="flex-shrink-0 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center"
                :class="
                  level.selected
                    ? 'border-brand-primary'
                    : 'border-text-secondary/40'
                "
              >
                <span
                  v-if="level.selected"
                  class="w-1.5 h-1.5 rounded-full bg-brand-primary"
                />
              </span>
              <span
                class="text-xs leading-tight"
                :class="
                  level.selected
                    ? 'text-text-primary font-medium'
                    : 'text-text-secondary'
                "
              >
                {{ level.label }}
              </span>
            </div>
          </div>
        </li>

        <li class="space-y-2">
          <div class="flex items-center gap-2">
            <span
              class="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-semibold flex items-center justify-center"
            >
              5
            </span>
            <h3 class="text-sm font-semibold text-text-primary">
              {{ $t("api_key_step_paste_title") }}
            </h3>
          </div>
          <div class="pl-8">
            <ElInput
              v-model="apiToken"
              type="textarea"
              :placeholder="$t('enter_api_key')"
              :rows="4"
              class="api-token-input"
            />
          </div>
        </li>
      </ol>

      <div class="rounded-xl border border-primary-light-9 bg-bg-sky-blue/30 overflow-hidden">
        <button
          type="button"
          class="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-bg-sky-blue/60 transition-colors"
          :aria-expanded="showSecurity"
          @click="showSecurity = !showSecurity"
        >
          <i class="icon-lock text-brand-primary text-lg flex-shrink-0" />
          <span class="text-sm font-medium text-text-primary flex-1">
            {{ $t("api_key_security_title") }}
          </span>
          <i
            class="icon-chevron-down text-xl text-text-secondary transition-transform"
            :class="{ 'rotate-180': showSecurity }"
          />
        </button>

        <transition
          enter-active-class="transition-all duration-200 ease-out overflow-hidden"
          leave-active-class="transition-all duration-150 ease-in overflow-hidden"
          enter-from-class="opacity-0 max-h-0"
          enter-to-class="opacity-100 max-h-[400px]"
          leave-from-class="opacity-100 max-h-[400px]"
          leave-to-class="opacity-0 max-h-0"
        >
          <ul v-if="showSecurity" class="px-4 pb-3 space-y-2">
            <li
              v-for="point in securityPoints"
              :key="point"
              class="flex items-start gap-2 text-xs text-text-regular leading-relaxed"
            >
              <i
                class="icon-tick text-additional-green text-base leading-5 flex-shrink-0 mt-0.5"
              />
              <span>{{ point }}</span>
            </li>
          </ul>
        </transition>
      </div>

      <p class="text-xs text-text-secondary flex items-center gap-1.5">
        <i class="icon-clock text-sm" />
        {{ $t("api_token_lifetime_note") }}
      </p>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-1">
        <CButton type="default" @click="handleCancel">
          {{ $t("cancel") }}
        </CButton>
        <CButton
          type="primary"
          :disabled="!apiToken.trim()"
          :loading="isLoading"
          @click="handleSave"
        >
          <template #loading>
            {{ $t("saving") }}
          </template>
          <template #default>
            {{ $t("save") }} <i class="icon-tick text-2xl" />
          </template>
        </CButton>
      </div>
    </template>
  </CDialog>
</template>

<style scoped>
.api-token-input :deep(.el-textarea__inner) {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
}
</style>
